import React, { Suspense, lazy } from 'react';

import { ErrorContainer, LoadingContainer } from './PdfViewer.styled';

export type ControlsPosition = 'top' | 'bottom' | 'left' | 'right';

export type PdfViewerProps = {
  url: string;
  initialPage?: number;
  scale?: number;
  showControls?: boolean;
  controlsPosition?: ControlsPosition;
  noPadding?: boolean;
  noScroll?: boolean;
  onError?: () => void;
};

/**
 * O visualizador real vive em `PdfViewerContent` e e carregado sob demanda, DE PROPOSITO.
 *
 * O `pdfjs-dist` 4.x usa top-level await, e no webpack isso e contagioso: todo modulo que importa
 * um modulo async vira async tambem. Importado de forma estatica, o react-pdf transformava o barrel
 * `~/ui/components` -- e, por tabela, quase toda tela -- em modulo async. Isso reordena a avaliacao
 * dos modulos e quebrou a dependencia circular que o barrel ja tinha: `PopoverCard.styled.ts` roda
 * `styled(Button)` antes de o barrel atribuir `Button`, o que derruba o chunk inteiro com
 * "Cannot create styled-component for component: undefined" e deixa a tela de login em branco.
 *
 * Com o `lazy`, o pdfjs sai do grafo sincrono: o barrel volta a ser sincrono e o PDF so e baixado
 * quando alguem de fato abre um documento.
 */
const PdfViewerContent = lazy(() => import('./PdfViewerContent'));

type ChunkBoundaryProps = { onError?: () => void; children: React.ReactNode };

/**
 * O `Suspense` cobre a promise PENDENTE do chunk, nao a REJEITADA. Sem boundary, um chunk que nao
 * baixa (deploy novo invalidando o arquivo, rede caindo) sobe como erro nao capturado -- e como nao
 * ha nenhum Error Boundary neste app, o React desmonta a arvore inteira. Abrir um PDF derrubaria a
 * SPA toda, que e justamente a tela branca que o carregamento sob demanda veio resolver.
 *
 * O `onError` e repassado de proposito: quem consome ja sabe degradar sem o visualizador (o
 * FileSingle, por exemplo, troca por link de download).
 */
class PdfViewerChunkBoundary extends React.Component<ChunkBoundaryProps, { failed: boolean }> {
  constructor(props: ChunkBoundaryProps) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    this.props.onError?.();
  }

  render() {
    if (this.state.failed) {
      return (
        <ErrorContainer>
          <p>Nao foi possivel carregar o visualizador de PDF. Recarregue a pagina e tente de novo.</p>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export function PdfViewer(props: PdfViewerProps) {
  return (
    <PdfViewerChunkBoundary onError={props.onError}>
      <Suspense fallback={<LoadingContainer>Carregando visualizador...</LoadingContainer>}>
        <PdfViewerContent {...props} />
      </Suspense>
    </PdfViewerChunkBoundary>
  );
}
